import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';

/**
 * Suppléments en un clic (PARITE-5, démo facturation-ged/Supplements, RG-25), ajout au contrat 1.0.0. Réservés à l'équipe de bureau
 * (permissions de facturation existantes) : un client ne voit jamais un supplément, seulement son reste dû (BillingClientView).
 * Le catalogue est une donnée versionnée de l'API ; un montant à null attend la grille tarifaire et se saisit à chaque ligne.
 */
const Centimes = z.number().int().min(1).max(100_000_000);
const Code = z.string().regex(/^[a-z][a-z_]{1,48}$/);

export const SupplementCatalogItemSchema = z
  .object({ code: Code, label: z.string().min(1), unitAmountCents: Centimes.nullable() })
  .meta({ id: 'SupplementCatalogItem', description: 'Ligne du catalogue versionné ; montant null tant que la grille n’est pas reçue.' });

export const BillingSupplementSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    code: Code,
    label: z.string().min(1),
    unitAmountCents: Centimes,
    quantity: z.number().int().min(1).max(99),
    totalCents: z.number().int().min(1),
    billable: z.boolean(),
    note: z.string().nullable(),
    createdAt: InstantSchema,
    createdByDisplayName: z.string(),
    /** Repris par une facture émise : ne se retire plus (avoir). */
    invoiced: z.boolean(),
    rowVersion: z.number().int().min(1),
  })
  .meta({ id: 'BillingSupplement', description: 'Supplément du dossier (équipe de bureau).' });

export const BillingSupplementCreateSchema = z
  .object({
    code: Code,
    quantity: z.number().int().min(1).max(99).optional(),
    unitAmountCents: Centimes.optional(),
    billable: z.boolean().optional(),
    note: z.string().trim().max(200).optional(),
  })
  .meta({ id: 'BillingSupplementCreate', description: 'Une ligne du catalogue ; montant saisi si la grille ne le fixe pas.' });

export const BillingSupplementsSchema = z
  .object({
    catalogue: z.array(SupplementCatalogItemSchema),
    catalogueStatus: z.enum(['a_valider', 'valide']),
    supplements: z.array(BillingSupplementSchema),
    billableTotalCents: z.number().int().min(0),
    notBilledTotalCents: z.number().int().min(0),
    /** La règle « frais réels » est cochée dans le jeu courant : sans elle, rien de ce qui est ajouté n'est facturé. */
    fraisReelsActive: z.boolean(),
  })
  .meta({ id: 'BillingSupplements', description: 'Suppléments du dossier, catalogue et totaux (équipe de bureau).' });
